/**
 * Firebase operations for Track & Field Scoring
 * Uses the same Firebase project as the timing app
 */

import { initializeApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore'
import { generateUUID } from '../lib/uuid.js'

// Same Firebase config as timing app
const firebaseConfig = {
  apiKey: "AIzaSyA2oYJ5ecVjgCdBntKb9kcO_1zmqt1NpCc",
  authDomain: "manatiming.firebaseapp.com",
  projectId: "manatiming",
  storageBucket: "manatiming.firebasestorage.app",
  messagingSenderId: "356275735229",
  appId: "1:356275735229:web:dd58d3ad1aaf81a96885d4"
}

let app = null
let db = null

export function initFirebaseScoring() {
  try {
    app = initializeApp(firebaseConfig)
    db = getFirestore(app)
    console.log('Firebase Scoring initialized')
    return db
  } catch (err) {
    // App may already be initialized
    if (err.code === 'app/duplicate-app') {
      app = initializeApp(firebaseConfig, 'scoring')
      db = getFirestore(app)
      return db
    }
    console.error('Firebase init error:', err)
    return null
  }
}

// --- MEETS ---

export async function saveMeet(meet) {
  if (!db) return null
  const id = meet.id || generateUUID()
  const data = {
    id,
    name: meet.name || '',
    date: meet.date || new Date().toISOString().split('T')[0],
    venue: meet.venue || '',
    schools: meet.schools || [],
    status: meet.status || 'setup',
    createdAt: meet.createdAt || Date.now(),
    updatedAt: Date.now()
  }
  await setDoc(doc(db, 'trackMeets', id), data, { merge: true })
  return data
}

export async function getMeet(meetId) {
  if (!db) return null
  const snap = await getDoc(doc(db, 'trackMeets', meetId))
  return snap.exists() ? snap.data() : null
}

export function subscribeToMeets(callback) {
  if (!db) return null
  return onSnapshot(collection(db, 'trackMeets'), (snapshot) => {
    const meets = []
    snapshot.forEach(d => meets.push(d.data()))
    meets.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    callback(meets)
  })
}

export async function deleteMeet(meetId) {
  if (!db) return false
  const batch = writeBatch(db)

  // Delete all events for this meet
  const eventsQ = query(collection(db, 'trackEvents'), where('meetId', '==', meetId))
  const eventsSnap = await getDocs(eventsQ)
  eventsSnap.forEach(d => batch.delete(doc(db, 'trackEvents', d.id)))

  // Delete all results for this meet
  const resultsQ = query(collection(db, 'trackResults'), where('meetId', '==', meetId))
  const resultsSnap = await getDocs(resultsQ)
  resultsSnap.forEach(d => batch.delete(doc(db, 'trackResults', d.id)))

  // Delete the meet itself
  batch.delete(doc(db, 'trackMeets', meetId))

  await batch.commit()
  return true
}

// --- EVENTS ---

export async function saveEvent(event) {
  if (!db) return null
  const id = event.id || generateUUID()
  const data = {
    id,
    meetId: event.meetId,
    division: event.division,
    eventCode: event.eventCode,
    eventName: event.eventName,
    eventType: event.eventType || 'individual',
    sortOrder: event.sortOrder || 0,
    status: event.status || 'pending',
    updatedAt: Date.now()
  }
  await setDoc(doc(db, 'trackEvents', id), data, { merge: true })
  return data
}

export async function createEventsForMeet(meetId, division, events) {
  if (!db) return []
  const batch = writeBatch(db)
  const created = []

  for (const ev of events) {
    const id = generateUUID()
    const data = {
      id,
      meetId,
      division,
      eventCode: ev.code,
      eventName: ev.name,
      eventType: ev.type,
      sortOrder: ev.order,
      status: 'pending',
      updatedAt: Date.now()
    }
    batch.set(doc(db, 'trackEvents', id), data)
    created.push(data)
  }

  await batch.commit()
  return created
}

export function subscribeToEvents(meetId, division, callback) {
  if (!db) return null
  const q = query(
    collection(db, 'trackEvents'),
    where('meetId', '==', meetId),
    where('division', '==', division)
  )
  return onSnapshot(q, (snapshot) => {
    const events = []
    snapshot.forEach(d => events.push(d.data()))
    events.sort((a, b) => a.sortOrder - b.sortOrder)
    callback(events)
  })
}

// --- RESULTS ---

export async function saveResults(meetId, eventId, results) {
  if (!db) return false
  const batch = writeBatch(db)

  // Delete existing results for this event first
  const existingQ = query(
    collection(db, 'trackResults'),
    where('meetId', '==', meetId),
    where('eventId', '==', eventId)
  )
  const existingSnap = await getDocs(existingQ)
  existingSnap.forEach(d => batch.delete(doc(db, 'trackResults', d.id)))

  // Save new results
  for (const r of results) {
    const id = generateUUID()
    batch.set(doc(db, 'trackResults', id), {
      id,
      meetId,
      eventId,
      place: r.place,
      school: r.school,
      updatedAt: Date.now()
    })
  }

  await batch.commit()
  return true
}

export function subscribeToResults(meetId, callback) {
  if (!db) return null
  const q = query(
    collection(db, 'trackResults'),
    where('meetId', '==', meetId)
  )
  return onSnapshot(q, (snapshot) => {
    const results = []
    snapshot.forEach(d => results.push(d.data()))
    callback(results)
  })
}

export async function getResultsForEvent(meetId, eventId) {
  if (!db) return []
  const q = query(
    collection(db, 'trackResults'),
    where('meetId', '==', meetId),
    where('eventId', '==', eventId)
  )
  const snap = await getDocs(q)
  const results = []
  snap.forEach(d => results.push(d.data()))
  results.sort((a, b) => a.place - b.place)
  return results
}
